import {
  Avatar,
  Box,
  Button,
  chakra,
  Divider,
  Flex,
  HStack,
  Stack,
  Tag,
  Text,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';
import { toUpper } from 'lodash';
import { Fragment, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  AccessLinkTypes,
  MeetingFieldsFragment,
  MeetingRelationCaseClusterFragment,
  MeetingRelationClientsFragment,
  MeetingRelationHostFieldsFragment,
  MeetingRelationHostFragment,
  MeetingRelationMeetingStatsFragment,
} from '@/@generated/codegen/API';
import ClientTag from '@/components/ClientTag/ClientTag';

import { AccessLinkPanelClient } from '../AccessLinkPanel/AccessLinkPanelClient';
import { useLoggedInUserAuthContext } from '../AuthTokenContextProvider/UserAuthContext';
import CaseClusterTag from '../CaseClusterTag/CaseClusterTag';
import { MeetingStatusTag } from '../MeetingStatusTag/MeetingStatusTag';

export type MeetingListRowType = MeetingFieldsFragment &
  MeetingRelationCaseClusterFragment &
  MeetingRelationHostFragment &
  MeetingRelationClientsFragment &
  MeetingRelationMeetingStatsFragment &
  MeetingRelationHostFieldsFragment;

type ListProps = {
  showID?: boolean;
  showMailDomain?: boolean;
  showGuestUrl?: boolean;
  data: MeetingListRowType[];
  actions?: Array<{
    render: (data: MeetingListRowType) => ReactNode;
  }>;
  title?: string;
  hideDate?: boolean;
  meetingToStatsExist?: Record<string, boolean>;
};

const MeetingListView = ({
  data,
  showID,
  showGuestUrl,
  showMailDomain,
  actions,
  title,
  hideDate,
  meetingToStatsExist,
}: ListProps) => {
  const { t } = useTranslation();
  const backgroundColor = useColorModeValue('gray.200', 'gray.700');
  const grayColor = useColorModeValue('gray.600', 'gray.300');
  const { userID } = useLoggedInUserAuthContext();

  const [managedMeetingID, setManagedMeetingID] = useState<string | undefined>(undefined);
  const [showAccessLinkPanel, setShowAccessLinkPanel] = useState<boolean>(false);

  return (
    <>
      {title && (
        <Flex justify="left" mb={3}>
          <chakra.h3 fontSize="2xl" fontWeight="bold" textAlign="center">
            {title}
          </chakra.h3>
        </Flex>
      )}
      {showAccessLinkPanel && managedMeetingID && (
        <AccessLinkPanelClient
          type={AccessLinkTypes.MeetingReadAccess}
          objId={managedMeetingID}
          onClose={() => {
            setManagedMeetingID(undefined);
            setShowAccessLinkPanel(false);
          }}
        />
      )}
      <VStack border="1px solid" borderColor="gray.200" rounded="md" spacing={0} background="white">
        {data.map((el, index) => {
          const archivedProps = !el.isArchived
            ? {}
            : {
                bg: 'gray.200',
                opacity: '0.5',
                _hover: {
                  opacity: '1.0',
                },
              };
          const isCancelled = el.isArchived && !!meetingToStatsExist && !meetingToStatsExist[el.id];

          return (
            <Fragment key={index}>
              <Stack
                w="full"
                direction={{ base: 'column', md: 'row' }}
                p={{ base: 4, md: 4 }}
                gap={3}
                justifyContent="space-between"
                bg={el.isDemo ? 'blue.100' : 'white'}
                _hover={{ bg: backgroundColor }}
                position="relative"
                {...archivedProps}
              >
                {el.isArchived && (
                  <Text
                    position="absolute"
                    left="0"
                    right="0"
                    textAlign="center"
                    color="livelinx.red"
                    opacity="1.0"
                  >
                    ARCHIVED {isCancelled ? '/ CANCELLED' : ''}
                  </Text>
                )}
                <Box gridColumnEnd={{ base: 'span 2', md: 'unset' }}>
                  <Flex alignItems="center" flexWrap="wrap">
                    <Avatar name={el.host.email || 'unknown'} color="white" size="xs" />

                    <Text pl={4} pr={2}>
                      {el.name}
                    </Text>
                    <CaseClusterTag name={el.caseCluster?.name || ''} />

                    {el.clients.edges.length !== 0 && (
                      <ClientTag client={el.clients.edges.map((el) => el.node.name).join(', ')} />
                    )}

                    <MeetingStatusTag meeting={el} />
                  </Flex>

                  <Box ml={5} pl={5}>
                    <HStack>
                      <Text>Host : </Text>
                      <chakra.p fontWeight="medium" fontSize="sm" color={grayColor}>
                        {showMailDomain ? el.host.email : el.host.email.split('@')[0]}
                      </chakra.p>
                      {el.hostId === userID && (
                        <Tag size="sm" colorScheme="blue">
                          {toUpper(t('common.you') || '')}
                        </Tag>
                      )}
                    </HStack>
                    {showID && (
                      <HStack>
                        <Text>ID : </Text>
                        <chakra.p fontWeight="medium" fontSize="sm" color={grayColor}>
                          {el.id}
                        </chakra.p>
                      </HStack>
                    )}
                    {showGuestUrl && (
                      <HStack>
                        <Text>Access links : </Text>
                        <Button
                          size="xs"
                          onClick={() => {
                            setManagedMeetingID(el.id);
                            setShowAccessLinkPanel(true);
                          }}
                          aria-label="View links"
                        >
                          View links
                        </Button>
                      </HStack>
                    )}
                    {el.eventDate && (
                      <HStack>
                        <Text>Event date : </Text>
                        <chakra.p fontWeight="medium" fontSize="sm" color={grayColor}>
                          {new Date(el.eventDate).toLocaleString()}{' '}
                        </chakra.p>
                      </HStack>
                    )}
                    {el.createdAt && hideDate && (
                      <HStack>
                        <Text> CREATION DATE :</Text>
                        <chakra.p fontWeight="medium" fontSize="sm" color={grayColor}>
                          {new Date(el.createdAt).toLocaleString()}
                        </chakra.p>
                      </HStack>
                    )}
                  </Box>
                </Box>

                <Stack
                  spacing={2}
                  direction="row"
                  fontSize={{ base: 'sm' }}
                  justifySelf="flex-end"
                  alignItems="right"
                >
                  {actions &&
                    actions.map((component, index: number) => (
                      <Fragment key={index}>{component.render(el)}</Fragment>
                    ))}
                </Stack>
              </Stack>
              {data.length - 1 !== index && <Divider m={0} />}
            </Fragment>
          );
        })}
      </VStack>
    </>
  );
};

export default MeetingListView;
